import { useEffect, useCallback } from 'react';

/**
 * useSwipeBack — iOS-like left-edge swipe gesture recognizer
 *
 * Detects a horizontal swipe starting from the left edge of the screen
 * and calls onBack() on success. Does NOT use history.back().
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether the gesture is active
 * @param {Function} options.onBack - Explicit navigation callback (no history)
 */
export function useSwipeBack({ enabled, onBack }) {
  const stableOnBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  useEffect(() => {
    if (!enabled) return;

    let startX = null;
    let startY = null;
    let tracking = false;
    let triggered = false;

    function onTouchStart(e) {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > 24) return; // left-edge only (24px)
      tracking = true;
      triggered = false;
      startX = t.clientX;
      startY = t.clientY;
    }

    function onTouchMove(e) {
      if (!tracking || triggered) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dy > 35) { tracking = false; return; } // vertical scroll — abort
      if (dx > 70) {
        triggered = true;
        tracking = false;
        stableOnBack();
      }
    }

    function onTouchEnd() {
      tracking = false;
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [enabled, stableOnBack]);
}
